import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Play,
  Clock,
  Calendar,
  Activity,
  Hourglass,
  Send,
  CircleDashed,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/lib/supabase'

interface CronJobStatusRow {
  jobid: number | null
  jobname: string
  schedule: string | null
  command: string | null
  active: boolean | null
  database: string | null
  username: string | null
  exists_flag: boolean
}

interface CronRunRow {
  runid: number
  jobid: number
  status: string
  return_message: string | null
  start_time: string
  end_time: string | null
  duration_ms: number | null
}

interface PendingRow {
  pending_count: number
  due_now_count: number
  processing_count: number
  failed_24h_count: number
  sent_24h_count: number
  oldest_due_at: string | null
  newest_due_at: string | null
}

const REFRESH_INTERVAL_MS = 30_000

export default function CheckScheduledMessages() {
  const [status, setStatus] = useState<CronJobStatusRow | null>(null)
  const [runs, setRuns] = useState<CronRunRow[]>([])
  const [pending, setPending] = useState<PendingRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [manualRunResult, setManualRunResult] = useState<{ rows: number; at: Date } | null>(null)
  const isMountedRef = useRef(true)

  const fetchAll = useCallback(async () => {
    if (!supabase) return
    setRefreshing(true)
    setError(null)

    try {
      const [statusRes, runsRes, pendingRes] = await Promise.all([
        supabase.rpc('check_scheduled_msgs_cron_status'),
        supabase.rpc('check_scheduled_msgs_recent_runs', { p_limit: 20 }),
        supabase.rpc('count_pending_scheduled_messages'),
      ])

      if (statusRes.error) throw statusRes.error
      if (runsRes.error) throw runsRes.error
      if (pendingRes.error) throw pendingRes.error

      if (!isMountedRef.current) return

      const statusRow = (statusRes.data as CronJobStatusRow[] | null)?.[0] ?? null
      const runRows = (runsRes.data as CronRunRow[] | null) ?? []
      const pendingRow = (pendingRes.data as PendingRow[] | null)?.[0] ?? null

      setStatus(statusRow)
      setRuns(runRows)
      setPending(pendingRow)
      setLastRefreshAt(new Date())
    } catch (err: any) {
      console.error('[CheckScheduledMessages] Erro ao carregar dados:', err)
      if (isMountedRef.current) {
        setError(err?.message || 'Erro ao consultar status do cron de mensagens agendadas')
      }
    } finally {
      if (isMountedRef.current) {
        setRefreshing(false)
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    fetchAll()
    return () => {
      isMountedRef.current = false
    }
  }, [fetchAll])

  useEffect(() => {
    if (!autoRefresh) return
    const id = window.setInterval(() => {
      fetchAll()
    }, REFRESH_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [autoRefresh, fetchAll])

  const handleRunNow = useCallback(async () => {
    if (!supabase || running) return
    setRunning(true)
    setError(null)
    try {
      const { data, error: runErr } = await supabase.rpc('run_dispatch_scheduled_messages')
      if (runErr) throw runErr
      const affected = typeof data === 'number' ? data : 0
      setManualRunResult({ rows: affected, at: new Date() })
      await fetchAll()
    } catch (err: any) {
      console.error('[CheckScheduledMessages] Erro ao rodar manualmente:', err)
      setError(err?.message || 'Erro ao rodar o dispatcher manualmente')
    } finally {
      if (isMountedRef.current) setRunning(false)
    }
  }, [running, fetchAll])

  const checks = useMemo(() => buildHealthChecks({ status, runs, pending }), [status, runs, pending])
  const overallOk = checks.every((c) => c.ok)

  return (
    <div className="w-full p-6 flex flex-col gap-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Check Mensagens Agendadas</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Status do job <span className="font-mono">dispatch-scheduled-messages</span>, executado por
            pg_cron a cada minuto. Lê <span className="font-mono">account_scheduled_messages</span>{' '}
            (status <span className="font-mono">pending</span> e <span className="font-mono">scheduled_at ≤ NOW()</span>),
            renderiza as variáveis e chama a edge function de envio conforme a versão da conexão
            (<span className="font-mono">send-message</span> / <span className="font-mono">send-message-v2</span> / <span className="font-mono">send-message-v3</span>).
          </p>
          {lastRefreshAt && (
            <p className="text-xs text-neutral-500 mt-1">
              Última atualização: {lastRefreshAt.toLocaleTimeString('pt-BR')}
              {autoRefresh && <span className="ml-2 text-neutral-600">(auto-refresh a cada 30s)</span>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setAutoRefresh((v) => !v)}
            className={autoRefresh ? 'bg-neutral-700 hover:bg-neutral-600' : 'bg-neutral-800 hover:bg-neutral-700'}
          >
            <Activity className="w-4 h-4 mr-2" />
            {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
          </Button>
          <Button onClick={fetchAll} disabled={refreshing} className="bg-neutral-700 hover:bg-neutral-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={handleRunNow} disabled={running} className="bg-blue-600 hover:bg-blue-700">
            <Play className={`w-4 h-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Executando...' : 'Executar agora'}
          </Button>
        </div>
      </header>

      {error && (
        <Card className="bg-red-900/20 border-red-700 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400">{error}</span>
          </div>
        </Card>
      )}

      {manualRunResult && (
        <Card className="bg-blue-900/20 border-blue-700 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300">
              Execução manual em {manualRunResult.at.toLocaleTimeString('pt-BR')}: <strong>{manualRunResult.rows}</strong>{' '}
              {manualRunResult.rows === 1 ? 'mensagem despachada' : 'mensagens despachadas'}.
            </span>
          </div>
        </Card>
      )}

      <Card
        className={`p-6 border ${overallOk ? 'bg-green-900/10 border-green-700' : 'bg-yellow-900/10 border-yellow-700'}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            {overallOk ? (
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            )}
            {overallOk ? 'Tudo funcional' : 'Atenção'}
          </h2>
          <Badge className={overallOk ? 'bg-green-600' : 'bg-yellow-600'}>
            {checks.filter((c) => c.ok).length} / {checks.length} checks OK
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {checks.map((c) => (
            <div
              key={c.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                c.ok ? 'bg-green-900/10 border-green-700/40' : 'bg-yellow-900/10 border-yellow-700/40'
              }`}
            >
              {c.ok ? (
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{c.title}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Configuração do Job
          </h2>
          {loading && !status ? (
            <SkeletonRows rows={5} />
          ) : status?.exists_flag ? (
            <div className="space-y-2 text-sm">
              <KeyValue label="Nome" value={<span className="font-mono text-white">{status.jobname}</span>} />
              <KeyValue label="Job ID" value={<span className="font-mono text-white">{status.jobid}</span>} />
              <KeyValue
                label="Agendamento"
                value={<span className="font-mono text-white">{status.schedule}</span>}
              />
              <KeyValue
                label="Ativo"
                value={
                  <Badge className={status.active ? 'bg-green-600' : 'bg-red-600'}>
                    {status.active ? 'true' : 'false'}
                  </Badge>
                }
              />
              <KeyValue label="Database" value={<span className="text-white">{status.database || '—'}</span>} />
              <KeyValue label="Usuário" value={<span className="text-white">{status.username || '—'}</span>} />
              <div className="pt-2">
                <p className="text-neutral-400 mb-1">Comando:</p>
                <pre className="bg-neutral-900 p-3 rounded-md text-xs text-green-400 font-mono overflow-x-auto">
                  {status.command}
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-300">
              <p className="font-semibold">Job não encontrado em cron.job.</p>
              <p className="mt-1 text-red-400/80">
                Verifique se a migration <span className="font-mono">20260513000000_create_account_scheduled_messages.sql</span>{' '}
                rodou e se as configurações <span className="font-mono">app.supabase_url</span> e{' '}
                <span className="font-mono">app.service_role_key</span> foram aplicadas via{' '}
                <span className="font-mono">ALTER DATABASE</span>.
              </p>
            </div>
          )}
        </Card>

        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Hourglass className="w-5 h-5 text-yellow-400" />
            Fila de mensagens agendadas
          </h2>
          {loading && !pending ? (
            <SkeletonRows rows={4} />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <Stat label="Pendentes" value={pending?.pending_count ?? 0} icon={<Clock className="w-4 h-4" />} accent="amber" />
                <Stat label="Vencidas (atrasadas)" value={pending?.due_now_count ?? 0} icon={<AlertCircle className="w-4 h-4" />} accent={(pending?.due_now_count ?? 0) > 0 ? 'red' : 'green'} />
                <Stat label="Processando" value={pending?.processing_count ?? 0} icon={<CircleDashed className="w-4 h-4" />} accent="blue" />
                <Stat label="Enviadas (24h)" value={pending?.sent_24h_count ?? 0} icon={<Send className="w-4 h-4" />} accent="green" />
                <Stat label="Falhas (24h)" value={pending?.failed_24h_count ?? 0} icon={<XCircle className="w-4 h-4" />} accent={(pending?.failed_24h_count ?? 0) > 0 ? 'red' : 'green'} />
              </div>
              <p className="text-xs text-neutral-500 mb-3">
                Em estado saudável <span className="font-mono">due_now_count = 0</span> — o dispatcher é executado a cada minuto.
              </p>
              {(pending?.pending_count ?? 0) > 0 && (
                <div className="space-y-1 text-xs text-neutral-400">
                  <KeyValue
                    label="Próximo envio em"
                    value={
                      <span className="text-yellow-300">
                        {pending?.oldest_due_at
                          ? new Date(pending.oldest_due_at).toLocaleString('pt-BR')
                          : '—'}
                      </span>
                    }
                  />
                  <KeyValue
                    label="Mais distante em"
                    value={
                      <span className="text-yellow-300">
                        {pending?.newest_due_at
                          ? new Date(pending.newest_due_at).toLocaleString('pt-BR')
                          : '—'}
                      </span>
                    }
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-purple-400" />
          Últimas execuções
        </h2>
        {loading && runs.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : runs.length === 0 ? (
          <div className="bg-neutral-900 p-6 rounded-lg text-center text-neutral-400 text-sm">
            Nenhuma execução registrada ainda. Aguarde até o próximo minuto e clique em <strong>Atualizar</strong>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-400 border-b border-neutral-700">
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Início</th>
                  <th className="py-2 pr-4 font-medium">Duração</th>
                  <th className="py-2 pr-4 font-medium">Run ID</th>
                  <th className="py-2 font-medium">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.runid} className="border-b border-neutral-800 last:border-b-0 align-top">
                    <td className="py-2 pr-4">
                      <Badge className={statusBadgeClass(r.status)}>{r.status}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-neutral-200 whitespace-nowrap">
                      {new Date(r.start_time).toLocaleString('pt-BR')}
                    </td>
                    <td className="py-2 pr-4 text-neutral-300 whitespace-nowrap">
                      {r.duration_ms === null ? '—' : `${r.duration_ms} ms`}
                    </td>
                    <td className="py-2 pr-4 text-neutral-400 font-mono text-xs">{r.runid}</td>
                    <td className="py-2 text-neutral-300 font-mono text-xs break-all">
                      {r.return_message || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function KeyValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <span className="text-neutral-400">{label}:</span>
      <span className="text-right">{value}</span>
    </div>
  )
}

function SkeletonRows({ rows }: { rows: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-4 bg-neutral-700/50 rounded" />
      ))}
    </div>
  )
}

function Stat({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon: React.ReactNode
  accent: 'amber' | 'red' | 'blue' | 'green'
}) {
  const accentClass = {
    amber: 'text-amber-300 border-amber-600/30 bg-amber-500/5',
    red: 'text-red-300 border-red-600/40 bg-red-500/5',
    blue: 'text-blue-300 border-blue-600/30 bg-blue-500/5',
    green: 'text-green-300 border-green-600/30 bg-green-500/5',
  }[accent]
  return (
    <div className={`rounded-lg border p-3 ${accentClass}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  )
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase()
  if (s === 'succeeded') return 'bg-green-600'
  if (s === 'failed') return 'bg-red-600'
  if (s === 'running' || s === 'starting') return 'bg-blue-600'
  return 'bg-neutral-600'
}

interface HealthCheck {
  id: string
  title: string
  detail: string
  ok: boolean
}

function buildHealthChecks(input: {
  status: CronJobStatusRow | null
  runs: CronRunRow[]
  pending: PendingRow | null
}): HealthCheck[] {
  const checks: HealthCheck[] = []

  const exists = !!input.status?.exists_flag
  checks.push({
    id: 'job_exists',
    title: 'Job registrado em cron.job',
    detail: exists
      ? `Job '${input.status?.jobname}' encontrado (id ${input.status?.jobid}).`
      : "Job não encontrado. Verifique se a migration 20260513000000 rodou e se pg_cron está habilitado.",
    ok: exists,
  })

  const isActive = !!input.status?.active
  checks.push({
    id: 'job_active',
    title: 'Job está ativo',
    detail: isActive ? 'cron.job.active = true.' : 'cron.job.active = false. O agendador não vai disparar este job.',
    ok: isActive,
  })

  const expectedSchedule = '* * * * *'
  const scheduleOk = input.status?.schedule === expectedSchedule
  checks.push({
    id: 'job_schedule',
    title: `Agendamento '${expectedSchedule}'`,
    detail: scheduleOk
      ? 'Schedule está como esperado (a cada minuto).'
      : `Schedule atual: '${input.status?.schedule ?? '—'}'. Esperado '${expectedSchedule}'.`,
    ok: scheduleOk,
  })

  const lastRun = input.runs[0]
  let lastRunOk = false
  let lastRunDetail = 'Nenhuma execução registrada ainda.'
  if (lastRun) {
    const ageMs = Date.now() - new Date(lastRun.start_time).getTime()
    const minutesAgo = Math.floor(ageMs / 60_000)
    const recent = ageMs <= 3 * 60_000
    lastRunOk = recent
    lastRunDetail = `Última execução há ${minutesAgo} minuto(s) (${new Date(lastRun.start_time).toLocaleTimeString('pt-BR')}).`
  }
  checks.push({
    id: 'recent_run',
    title: 'Executou nos últimos 3 minutos',
    detail: lastRunDetail,
    ok: lastRunOk,
  })

  const lastSucceeded = lastRun?.status?.toLowerCase() === 'succeeded'
  checks.push({
    id: 'last_run_succeeded',
    title: 'Última execução foi bem-sucedida',
    detail: lastRun
      ? `Status: ${lastRun.status}. Mensagem: ${lastRun.return_message || '—'}`
      : 'Sem execuções para avaliar.',
    ok: lastSucceeded,
  })

  const lastTen = input.runs.slice(0, 10)
  const failed = lastTen.filter((r) => r.status?.toLowerCase() !== 'succeeded').length
  const noRecentFailures = failed === 0 && lastTen.length > 0
  checks.push({
    id: 'no_recent_failures',
    title: 'Sem falhas nas últimas 10 execuções',
    detail:
      lastTen.length === 0
        ? 'Sem execuções para avaliar.'
        : noRecentFailures
          ? 'Todas as últimas 10 execuções terminaram em succeeded.'
          : `${failed}/${lastTen.length} execuções falharam recentemente.`,
    ok: noRecentFailures,
  })

  const dueNow = input.pending?.due_now_count ?? 0
  checks.push({
    id: 'no_due_now',
    title: 'Sem mensagens vencidas pendentes',
    detail:
      dueNow === 0
        ? 'Nenhuma mensagem vencida aguardando envio.'
        : `${dueNow} mensagem(ns) com scheduled_at <= now() ainda em pending.`,
    ok: dueNow === 0,
  })

  return checks
}
